// =============================================================================
// FILE: packages/renderer/src/components/ui/Calendar.tsx
// PURPOSE: Elegant, dependency-free month calendar with a date-picker variant.
//          Pure React + Tailwind — no shadcn, no date-fns, no popper. Drops
//          straight into any page or modal.
//
// Exports:
//   <Calendar value? onChange? minDate? maxDate? />        ← inline calendar
//   <DatePicker value? onChange? placeholder? />           ← input + popover
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const x = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${x}`;
}
function fromYmd(s?: string | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth()    === b.getMonth()
      && a.getDate()     === b.getDate();
}

interface CalendarProps {
  /** YYYY-MM-DD or null. */
  value?: string | null;
  onChange?: (value: string) => void;
  minDate?: string;
  maxDate?: string;
  className?: string;
}

export function Calendar({ value, onChange, minDate, maxDate, className }: CalendarProps): JSX.Element {
  const today = useMemo(() => new Date(), []);
  const selected = fromYmd(value);
  const [cursor, setCursor] = useState<Date>(() => selected ?? today);

  useEffect(() => { if (selected) setCursor(selected); }, [value]);

  const min = fromYmd(minDate);
  const max = fromYmd(maxDate);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const leadingBlanks = monthStart.getDay();
  const cells: Array<{ d: Date | null }> = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push({ d: null });
  for (let i = 1; i <= daysInMonth; i++) cells.push({ d: new Date(cursor.getFullYear(), cursor.getMonth(), i) });
  while (cells.length % 7 !== 0) cells.push({ d: null });

  const stepMonth = (delta: number): void => {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  };
  const onPick = (d: Date): void => {
    if (min && d < min) return;
    if (max && d > max) return;
    onChange?.(ymd(d));
  };

  return (
    <div
      data-testid="calendar-widget"
      className={['select-none rounded-sm border border-zinc-800 bg-zinc-950 p-2 shadow-xl w-full max-w-[280px] mx-auto', className ?? ''].join(' ')}
    >
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          data-testid="calendar-prev"
          onClick={() => stepMonth(-1)}
          aria-label="Previous month"
          className="p-1 rounded-sm text-zinc-400 hover:text-amber-300 hover:bg-zinc-900 transition"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div data-testid="calendar-month-label" className="text-[12px] font-medium tracking-wide text-zinc-200">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </div>
        <button
          type="button"
          data-testid="calendar-next"
          onClick={() => stepMonth(1)}
          aria-label="Next month"
          className="p-1 rounded-sm text-zinc-400 hover:text-amber-300 hover:bg-zinc-900 transition"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 text-center text-[9px] uppercase tracking-[0.14em] text-zinc-500 mb-1">
        {WEEKDAYS.map((w, i) => <div key={`${w}-${i}`}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((c, i) => {
          if (!c.d) return <div key={`blank-${i}`} className="h-7" />;
          const isSelected = selected && sameDay(c.d, selected);
          const isToday    = sameDay(c.d, today);
          const disabled   = (min && c.d < min) || (max && c.d > max);
          return (
            <button
              key={ymd(c.d)}
              type="button"
              data-testid={`calendar-day-${ymd(c.d)}`}
              disabled={!!disabled}
              onClick={() => onPick(c.d!)}
              className={[
                'h-7 w-full rounded-sm text-[11.5px] font-medium transition focus:outline-none',
                isSelected
                  ? 'bg-amber-500 text-zinc-950 shadow-[0_0_0_1px_rgba(245,158,11,.4)]'
                  : isToday
                    ? 'text-amber-300 ring-1 ring-amber-500/40 hover:bg-zinc-900'
                    : 'text-zinc-300 hover:bg-zinc-900',
                disabled ? 'opacity-30 cursor-not-allowed hover:bg-transparent' : '',
              ].join(' ')}
            >
              {c.d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[9.5px] uppercase tracking-[0.14em] text-zinc-500">
        <button
          type="button"
          data-testid="calendar-today"
          onClick={() => { onPick(today); setCursor(today); }}
          className="px-2 py-0.5 rounded-sm border border-zinc-800 hover:border-amber-500/40 hover:text-amber-200 transition"
        >
          Today
        </button>
        <span data-testid="calendar-selected-value">
          {selected ? ymd(selected) : 'No date'}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DatePicker — text input + click-out popover wrapper around <Calendar />.
// ---------------------------------------------------------------------------

interface DatePickerProps {
  value?: string | null;
  onChange?: (value: string) => void;
  placeholder?: string;
  testid?: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
}

export function DatePicker({
  value, onChange, placeholder = 'YYYY-MM-DD', testid = 'datepicker',
  className, minDate, maxDate,
}: DatePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={['relative inline-block', className ?? ''].join(' ')}>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-100 text-[13px] hover:border-amber-500/40 transition min-w-[160px]"
      >
        <CalendarIcon className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.75} />
        <span data-testid={`${testid}-value`} className={value ? '' : 'text-zinc-500'}>
          {value ?? placeholder}
        </span>
      </button>
      {open && (
        <div
          data-testid={`${testid}-popover`}
          className="absolute z-50 mt-1 left-0"
          role="dialog"
          aria-modal="false"
        >
          <Calendar
            value={value}
            onChange={(v) => { onChange?.(v); setOpen(false); }}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>
      )}
    </div>
  );
}
